import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import { CONVERSATION_QUERY_KEY } from "./conversation.query.key";
import type { TypedConversation } from "./conversation.types";

const ITEMS_PER_PAGE = 20;

export function useInfiniteConversations(searchQuery?: string) {
  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, refetch } =
    useInfiniteQuery({
      queryKey: [CONVERSATION_QUERY_KEY, "infinite", searchQuery],
      queryFn: async (args) => {
        const res = await api.conversationCrud.getAll({
          filter: searchQuery
            ? [
                {
                  OR: [
                    {
                      "participants.messageContact.displayName": {
                        operator: "contains",
                        value: searchQuery,
                      },
                      isArchived: { operator: "equals", value: false },
                    },
                    {
                      "participants.messageContact.phoneNumber": {
                        operator: "contains",
                        value: searchQuery,
                      },
                      isArchived: { operator: "equals", value: false },
                    },
                  ],
                },
              ]
            : {
                isArchived: { operator: "equals", value: false },
              },
          sort: { field: "lastMessageAt", order: "desc" },
          include: {
            participants: { select: true },
            windows: { select: true },
          },
          aggregate: {
            unreadMessages: { compute: true },
          },
          pagination: {
            take: ITEMS_PER_PAGE,
            page: args.pageParam + 1,
          },
        });
        return res.data;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const totalItems = lastPage.count;
        const currentItems = (lastPageParam + 1) * ITEMS_PER_PAGE;

        if (currentItems >= totalItems) {
          return undefined;
        }

        return lastPageParam + 1;
      },
    });

  const conversations = (data?.pages.flatMap((page) => page.items) ?? []) as TypedConversation[];

  const loadMore = async (ev: CustomEvent) => {
    await fetchNextPage();
    if (ev.target) {
      (ev.target as HTMLIonInfiniteScrollElement).complete();
    }
  };

  return {
    conversations,
    loadMore,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    refetch,
  };
}
